import { useStepBeneficiaryContingentStore } from "#src/stores/step-beneficiary-contingent.ts";
import { useStepOwnerStore } from "#src/stores/step-owner.ts";

import { STEP } from "#src/constants/step.ts";
import {
  defineStore,
  generateInitialState,
  resetState,
  type ToSetupState,
} from "#src/stores/state-wrapper.ts";
import {
  type DataRequirement,
  type DataRequirements,
  REQS,
} from "#src/constants/data-requirements.ts";
import { computed } from "vue";
import { type Pinia, type StoreGeneric } from "pinia";
import {
  type BaseStepStoreState,
  type BaseStepStore,
} from "#src/types/StepStore.ts";

function initializeRequiredFields(reqs?: DataRequirements) {
  const required = (requirement: DataRequirement): boolean =>
    reqs?.[requirement] || false;
  return {
    address: required(REQS.BENEFICIARY_ADDRESS),
    phone: required(REQS.BENEFICIARY_PHONE),
    tin: required(REQS.BENEFICIARY_TIN),
    formationDate: required(REQS.BENEFICIARY_FORMATION_DATE),
    birthdateOrSsn: required(REQS.BENEFICIARY_BIRTHDATE_OR_SSN),
    relationship: required(REQS.BENEFICIARY_RELATIONSHIP),
    birthdate: required(REQS.BENEFICIARY_BIRTHDATE),
    ssn: required(REQS.BENEFICIARY_SSN),
    ssnOptional: required(REQS.BENEFICIARY_SSN_OPTIONAL),
    gender: required(REQS.BENEFICIARY_GENDER),
    signerName: required(REQS.BENEFICIARY_SIGNER_NAME),
    signerEmail: required(REQS.BENEFICIARY_SIGNER_EMAIL),
    signerBirthdate: required(REQS.BENEFICIARY_SIGNER_BIRTHDATE),
    revocable: required(REQS.BENEFICIARY_REVOCABLE),
  };
}

interface PrimaryBeneficiaryState extends BaseStepStoreState {
  requiredFields: Record<string, boolean>;
}

function initialState(): PrimaryBeneficiaryState {
  return {
    inactive: false,
    requiredFields: initializeRequiredFields(),
  };
}

interface PrimaryBeneficiaryStep
  extends BaseStepStore,
    ToSetupState<PrimaryBeneficiaryState> {
  setStepRequirements(srq: DataRequirements): void;
}

function initializePrimaryBeneficiaryStep(
  pinia: Pinia
): PrimaryBeneficiaryStep {
  const state = generateInitialState<PrimaryBeneficiaryState>(initialState());
  function $reset() {
    resetState<PrimaryBeneficiaryState>(state, initialState());
  }

  const step = computed(() => STEP.PRIMARY_BENEFICIARY);
  const previous = computed(() => {
    const owner = useStepOwnerStore(pinia);
    if (owner.inactive) return owner.previous;
    return owner.step;
  });
  const next = computed(() => {
    const contingent = useStepBeneficiaryContingentStore(pinia);
    if (contingent.inactive) return contingent.next;
    return contingent.step;
  });

  function setStepRequirements(srq: DataRequirements) {
    state.requiredFields.value = initializeRequiredFields(srq);
    state.inactive.value = !Object.values(state.requiredFields.value).includes(
      true
    );
  }

  return {
    ...state,
    step,
    previous,
    next,
    setStepRequirements,
    $reset,
  };
}

export const useStepBeneficiaryPrimaryStore = (
  pinia: Pinia,
  hot?: StoreGeneric
) => {
  return defineStore<string, PrimaryBeneficiaryStep>(
    "step-beneficiary-primary",
    () => {
      return initializePrimaryBeneficiaryStep(pinia);
    }
  )(pinia, hot);
};
